import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { PortfolioItem } from '../services/types';
import './AssetDetailModal.css';
import { fmtTR } from '../utils/format';

interface AssetDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: PortfolioItem | null;
}

interface HistoricalData {
  date: string;
  price: number;
  change: number;
  changePercent: number;
}

type TimePeriod = '1D' | '1W' | '1M' | '6M' | '1Y' | '5Y';

const AssetDetailModal: React.FC<AssetDetailModalProps> = ({ isOpen, onClose, asset }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('1M');
  const [historicalData, setHistoricalData] = useState<HistoricalData[]>([]);
  const [loading, setLoading] = useState(false);

  const timePeriods: { value: TimePeriod; label: string; days: number }[] = [
    { value: '1D', label: 'Günlük', days: 1 },
    { value: '1W', label: 'Haftalık', days: 7 },
    { value: '1M', label: 'Aylık', days: 30 },
    { value: '6M', label: '6 Aylık', days: 180 },
    { value: '1Y', label: 'Yıllık', days: 365 },
    { value: '5Y', label: '5 Yıllık', days: 1825 }
  ];

  useEffect(() => {
    if (isOpen && asset) {
      fetchHistoricalData();
    }
  }, [isOpen, asset, selectedPeriod]);

  const fetchHistoricalData = async () => {
    if (!asset) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `/api/historical/${asset.type}/${encodeURIComponent(asset.symbol)}?period=${selectedPeriod}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setHistoricalData(data);
      } else {
        setHistoricalData([]);
      }
    } catch (error) {
      console.error('❌ Error fetching historical data:', error instanceof Error ? error.message : String(error));
      setHistoricalData([]);
    } finally {
      setLoading(false);
    }
  };

  const calculatePeriodStats = () => {
    if (historicalData.length === 0) return null;
    
    const startPrice = historicalData[0].price;
    const endPrice = historicalData[historicalData.length - 1].price;
    const change = endPrice - startPrice;
    const changePercent = (change / startPrice) * 100;
    
    const prices = historicalData.map(d => d.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const avgPrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
    
    return {
      startPrice,
      endPrice,
      change,
      changePercent,
      minPrice,
      maxPrice,
      avgPrice
    };
  };

  if (!isOpen || !asset) return null;

  const stats = calculatePeriodStats();

  // Downsample data for long periods to keep chart clean
  const getChartData = () => {
    const maxPoints: Record<TimePeriod, number> = {
      '1D': 100,
      '1W': 50,
      '1M': 31,
      '6M': 60,
      '1Y': 52,
      '5Y': 60,
    };
    const limit = maxPoints[selectedPeriod] || 60;
    if (historicalData.length <= limit) return historicalData;
    const step = (historicalData.length - 1) / (limit - 1);
    const sampled: HistoricalData[] = [];
    for (let i = 0; i < limit; i++) {
      sampled.push(historicalData[Math.round(i * step)]);
    }
    return sampled;
  };

  const chartData = getChartData();

  // Format X axis labels based on period
  const formatXTick = (val: string) => {
    const d = new Date(val);
    switch (selectedPeriod) {
      case '1D':
        return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
      case '1W':
      case '1M':
        return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
      case '6M':
        return d.toLocaleDateString('tr-TR', { month: 'short', year: '2-digit' });
      case '1Y':
        return d.toLocaleDateString('tr-TR', { month: 'short', year: '2-digit' });
      case '5Y':
        return d.toLocaleDateString('tr-TR', { month: 'short', year: 'numeric' });
      default:
        return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
    }
  };

  const getMinTickGap = () => {
    switch (selectedPeriod) {
      case '1D': return 40;
      case '1W': return 40;
      case '1M': return 50;
      case '6M': return 60;
      case '1Y': return 70;
      case '5Y': return 80;
      default: return 50;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content asset-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {asset.type === 'stock' && '📈'} 
            {asset.type === 'currency' && '💱'} 
            {asset.type === 'gold' && '🏅'} 
            {asset.type === 'crypto' && '🪙'} 
            {asset.symbol} - {asset.name}
          </h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="time-period-selector">
          {timePeriods.map(period => (
            <button
              key={period.value}
              className={`period-button ${selectedPeriod === period.value ? 'active' : ''}`}
              onClick={() => setSelectedPeriod(period.value)}
            >
              {period.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <>
            {stats && (
              <div className="period-stats">
                <div className="stats-grid">
                  <div className="stat-item">
                    <label>Başlangıç:</label>
                    <span>₺{fmtTR(stats.startPrice)}</span>
                  </div>
                  <div className="stat-item">
                    <label>Bitiş:</label>
                    <span>₺{fmtTR(stats.endPrice)}</span>
                  </div>
                  <div className="stat-item">
                    <label>Değişim:</label>
                    <span className={stats.change >= 0 ? 'positive' : 'negative'}>
                      {stats.change >= 0 ? '+' : ''}₺{fmtTR(Math.abs(stats.change))}
                    </span>
                  </div>
                  <div className="stat-item">
                    <label>Değişim %:</label>
                    <span className={stats.changePercent >= 0 ? 'positive' : 'negative'}>
                      {stats.changePercent >= 0 ? '+' : ''}{fmtTR(stats.changePercent)}%
                    </span>
                  </div>
                  <div className="stat-item">
                    <label>En Düşük:</label>
                    <span>₺{fmtTR(stats.minPrice)}</span>
                  </div>
                  <div className="stat-item">
                    <label>En Yüksek:</label>
                    <span>₺{fmtTR(stats.maxPrice)}</span>
                  </div>
                  <div className="stat-item">
                    <label>Ortalama:</label>
                    <span>₺{fmtTR(stats.avgPrice)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="historical-chart">
              <h3>Fiyat Grafiği</h3>
              <div className="chart-container">
                {historicalData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                      <defs>
                        <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={stats && stats.change >= 0 ? '#22c55e' : '#ef4444'} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={stats && stats.change >= 0 ? '#22c55e' : '#ef4444'} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatXTick}
                        tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                        axisLine={{ stroke: 'var(--border)' }}
                        tickLine={false}
                        interval="preserveStartEnd"
                        minTickGap={getMinTickGap()}
                      />
                      <YAxis
                        domain={['auto', 'auto']}
                        tickFormatter={(val: number) => `₺${val.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`}
                        tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                        axisLine={false}
                        tickLine={false}
                        width={70}
                      />
                      <Tooltip
                        contentStyle={{
                          background: 'var(--card)',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          fontSize: '0.85rem',
                        }}
                        labelFormatter={(label: any) => new Date(String(label)).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
                        formatter={(value: any) => [`₺${Number(value).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Fiyat']}
                      />
                      <Area
                        type="monotone"
                        dataKey="price"
                        stroke={stats && stats.change >= 0 ? '#22c55e' : '#ef4444'}
                        strokeWidth={2.5}
                        fill="url(#priceGradient)"
                        dot={false}
                        activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-chart-data">
                    <p>Bu dönem için tarihsel veri bulunamadı</p>
                  </div>
                )}
              </div>
            </div>

            <div className="historical-table">
              <h3>Historik Veriler</h3>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Fiyat</th>
                      <th>Değişim</th>
                      <th>Değişim %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historicalData.slice().reverse().slice(0, 10).map((data, index) => (
                      <tr key={index}>
                        <td>{new Date(data.date).toLocaleDateString('tr-TR')}</td>
                        <td>₺{fmtTR(data.price)}</td>
                        <td className={data.change >= 0 ? 'positive' : 'negative'}>
                          {data.change >= 0 ? '+' : ''}₺{fmtTR(Math.abs(data.change))}
                        </td>
                        <td className={data.changePercent >= 0 ? 'positive' : 'negative'}>
                          {data.changePercent >= 0 ? '+' : ''}{fmtTR(data.changePercent)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AssetDetailModal;
